<script setup lang="ts">
/**
 * A table's cells, and what can be done to the ones selected.
 *
 * Cells are edited in a grid that mirrors the table itself, so the thing being
 * edited looks like the thing on the page. Editing a financial table through a
 * list of fields is how a column ends up one row short.
 *
 * Beside the grid, a toolbar for whatever is selected in it: a cell, a row, a
 * column, a block of cells or the whole table. The same toolbar for all of
 * them, so colouring a total row is one click rather than one per cell, and so
 * there is one place to learn. Merging is in it too, offered only when the
 * selection holds something to merge or to split.
 *
 * Laid out two ways from one template, so the inspector and the table editor
 * cannot come to offer different things:
 *
 * - `panel`, for the inspector's narrow column: the toolbar above the grid.
 * - `wide`, for the table editor: a column of its own beside the grid, in
 *   three tabs (the cell tools, the table's look, its settings: the last two
 *   given as slots), with room under the grid for a preview. A toolbar beside
 *   the grid also never pushes the grid down as the selection changes, and
 *   picking a cell brings its tools forward.
 *
 * This file owns the selection and decides what each request means for the
 * table; the operations themselves live in `edit.ts`, where they are tested
 * without a browser. Nothing here can leave the table ragged, because nothing
 * here touches the cells directly.
 */
import { useI18n } from 'vue-i18n'

import { computed, ref, watch } from 'vue'

import { useRememberedTab } from '@/composables/useRememberedTab'
import { emptyCell, type CellAlign, type TableCell, type TableSection } from '@/domain/section'
import InspectorTabs from '@/sections/InspectorTabs.vue'
import { TAB_ICONS, type InspectorTab } from '@/sections/inspectorTabs'
import TableCellGrid from '@/sections/table/TableCellGrid.vue'
import TableCellToolbar from '@/sections/table/TableCellToolbar.vue'
import {
  areaOf,
  canMerge,
  canMoveColumn,
  canMoveRow,
  canSplit,
  clearCells,
  columnCount,
  columnIsNumeric,
  commonStyle,
  insertColumn,
  insertRow,
  mergeCells,
  moveColumn,
  moveRow,
  removeColumn,
  removeRow,
  setCells,
  setColumnNumeric,
  settleTarget,
  splitCells,
  textsLostByMerge,
  type CellPoint,
  type CellStyleChange,
  type CellTarget,
  type RowRef,
} from '@/sections/table/edit'
import { rowOf } from '@/sections/table/spans'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    section: TableSection
    layout?: 'panel' | 'wide'
  }>(),
  { layout: 'panel' },
)

const emit = defineEmits<{ change: [section: TableSection] }>()

defineSlots<{
  /** In the wide layout's side column: the table's look, and its own settings, each a tab. */
  look?: () => unknown
  settings?: () => unknown
  /**
   * Under the grid. Given what is selected, and a way to select a cell from
   * outside the grid, so a preview can show the selection and take clicks.
   */
  below?: (scope: {
    target: CellTarget | null
    pick: (point: CellPoint, extend: boolean) => void
  }) => unknown
}>()

const columns = computed(() => columnCount(props.section))

/**
 * The table's words and layout belong to CSRD, so they are not edited here.
 *
 * Refused at every handler, and not only hidden in the toolbar: the same
 * operations are reached from the grid, the toolbar's keys and the buttons
 * above it, and one left open is how a locked table stops being one.
 */
const locked = computed(() => Boolean(props.section.csrd))

/**
 * What the toolbar acts on.
 *
 * Kept here rather than in the grid because the toolbar needs it too, and
 * settled against the table on every read: a row that was selected and then
 * deleted is no longer anything to act on, and a block drawn across half of a
 * merged cell is taken to mean all of it.
 */
const selected = ref<CellTarget | null>(null)

const target = computed<CellTarget | null>(() =>
  selected.value ? settleTarget(props.section, selected.value) : null,
)

/** A different table is a different selection. */
watch(
  () => props.section.id,
  () => {
    selected.value = null
  },
)

const shared = computed(() => (target.value ? commonStyle(props.section, target.value) : {}))

/** Which column the selection sits on, for the figures toggle. */
const targetColumn = computed<number | null>(() => {
  const chosen = target.value
  return chosen && (chosen.scope === 'cell' || chosen.scope === 'column') ? chosen.column : null
})

const numeric = computed(() =>
  targetColumn.value === null ? false : columnIsNumeric(props.section, targetColumn.value),
)

/** The block the selection stands for, with any merged cell in it taken whole. */
const area = computed(() => (target.value ? areaOf(props.section, target.value) : null))

const mergeable = computed(() => (target.value ? canMerge(props.section, target.value) : false))
const splittable = computed(() => (target.value ? canSplit(props.section, target.value) : false))
const lost = computed(() => (target.value ? textsLostByMerge(props.section, target.value) : 0))

function replace(section: TableSection): void {
  emit('change', section)
}

/**
 * One cell's text.
 *
 * Pads the row out to the table's width first, so typing into a cell that a
 * ragged import never wrote still lands where it was typed.
 */
function editCell(row: RowRef, column: number, value: string): void {
  if (locked.value) return
  const rewrite = (cells: readonly TableCell[]): TableCell[] =>
    Array.from({ length: Math.max(cells.length, column + 1) }, (_, at) =>
      at === column ? { ...(cells[at] ?? emptyCell()), value } : (cells[at] ?? emptyCell()),
    )
  if (row === 'header') replace({ ...props.section, header: rewrite(props.section.header) })
  else {
    replace({
      ...props.section,
      rows: props.section.rows.map((cells, at) => (at === row ? rewrite(cells) : cells)),
    })
  }
}

/**
 * A cell chosen from outside the grid, a preview for one.
 *
 * With `extend`, the block from the corner the selection started at to this
 * cell, as Shift and a click does in the grid itself.
 */
function pick(point: CellPoint, extend: boolean): void {
  const current = target.value
  const from =
    current?.scope === 'cell'
      ? { row: current.row, column: current.column }
      : current?.scope === 'range'
        ? current.from
        : null
  selected.value =
    extend && from ? { scope: 'range', from, to: point } : { scope: 'cell', ...point }
}

function restyle(change: CellStyleChange): void {
  if (target.value) replace(setCells(props.section, target.value, change))
}

function alignTo(align: CellAlign | null): void {
  restyle({ align })
}

function setNumeric(on: boolean): void {
  if (targetColumn.value !== null) replace(setColumnNumeric(props.section, targetColumn.value, on))
}

/**
 * The body row the selection sits on, or where "below the header" lands.
 *
 * `last` is the row a new row below goes after: the selected row itself, or
 * for a merged cell the last row it runs down over, so a row added below a
 * tall merged label lands under the label rather than inside it.
 */
function selectedRow(): { at: number; last: number; onHeader: boolean } | null {
  const chosen = target.value
  if (!chosen || (chosen.scope !== 'cell' && chosen.scope !== 'row')) return null
  const bottom = chosen.scope === 'cell' && area.value ? area.value.bottom - 1 : null
  if (chosen.row === 'header') return { at: -1, last: bottom ?? -1, onHeader: true }
  return { at: chosen.row, last: bottom ?? chosen.row, onHeader: false }
}

/** Where the selected rows could move to, and whether a merge ties them in place. */
const movable = computed(() => {
  const row = selectedRow()
  const column = targetColumn.value
  const upDown = row && !row.onHeader ? row.at : null
  return {
    up: upDown !== null && canMoveRow(props.section, upDown, upDown - 1),
    down: upDown !== null && canMoveRow(props.section, upDown, upDown + 1),
    left: column !== null && canMoveColumn(props.section, column, column - 1),
    right: column !== null && canMoveColumn(props.section, column, column + 1),
  }
})

/** The selection, moved to follow a row that moved or was just made. */
function reselectRow(row: number): void {
  const chosen = target.value
  if (chosen?.scope === 'cell') selected.value = { scope: 'cell', row, column: chosen.column }
  else selected.value = { scope: 'row', row }
}

function reselectColumn(column: number): void {
  const chosen = target.value
  if (chosen?.scope === 'cell') selected.value = { scope: 'cell', row: chosen.row, column }
  else selected.value = { scope: 'column', column }
}

function addRow(where: 'above' | 'below'): void {
  if (locked.value) return
  const row = selectedRow()
  if (!row) return
  const at = where === 'above' ? row.at : row.last + 1
  replace(insertRow(props.section, at))
  reselectRow(at)
}

function shiftRow(by: -1 | 1): void {
  if (locked.value) return
  const row = selectedRow()
  if (!row || row.onHeader) return
  replace(moveRow(props.section, row.at, row.at + by))
  reselectRow(row.at + by)
}

function dropRow(): void {
  if (locked.value) return
  const row = selectedRow()
  if (!row || row.onHeader) return
  replace(removeRow(props.section, row.at))
  selected.value = null
}

function addColumn(where: 'left' | 'right'): void {
  if (locked.value) return
  const column = targetColumn.value
  if (column === null) return
  const right = target.value?.scope === 'cell' && area.value ? area.value.right : column
  const at = where === 'left' ? column : right + 1
  replace(insertColumn(props.section, at))
  reselectColumn(at)
}

function shiftColumn(by: -1 | 1): void {
  if (locked.value) return
  const column = targetColumn.value
  if (column === null) return
  replace(moveColumn(props.section, column, column + by))
  reselectColumn(column + by)
}

function dropColumn(): void {
  if (locked.value) return
  const column = targetColumn.value
  if (column === null) return
  replace(removeColumn(props.section, column))
  selected.value = null
}

function clear(): void {
  if (!locked.value && target.value) replace(clearCells(props.section, target.value))
}

/** The block made one cell, which is then what is selected. */
function merge(): void {
  if (locked.value) return
  const chosen = target.value
  const block = area.value
  if (!chosen || !block || !mergeable.value) return
  replace(mergeCells(props.section, chosen))
  selected.value = { scope: 'cell', row: rowOf(block.top), column: block.left }
}

/** Every merged cell in the selection split; the selection stays where it was. */
function split(): void {
  if (!locked.value && target.value) replace(splitCells(props.section, target.value))
}

/** What the toolbar is told about the selection, and what it asks for, wherever it stands. */
const toolbar = computed(() => ({
  target: target.value,
  shared: shared.value,
  numeric: numeric.value,
  rows: props.section.rows.length,
  columns: columns.value,
  movable: movable.value,
  mergeable: mergeable.value,
  splittable: splittable.value,
  lost: lost.value,
  locked: locked.value,
}))

const asks = {
  align: alignTo,
  strong: (on: boolean) => restyle({ strong: on ? true : null }),
  fill: (colour: string | null) => restyle({ fill: colour }),
  ink: (colour: string | null) => restyle({ ink: colour }),
  'rule-above': (on: boolean) => restyle({ ruleAbove: on ? true : null }),
  'rule-below': (on: boolean) => restyle({ ruleBelow: on ? true : null }),
  numeric: setNumeric,
  'insert-row': addRow,
  'insert-column': addColumn,
  'move-row': shiftRow,
  'move-column': shiftColumn,
  'remove-row': dropRow,
  'remove-column': dropColumn,
  clear,
  merge,
  split,
  deselect: () => (selected.value = null),
}

// ── The wide layout's tabs ──────────────────────────────────────────────

type SideTab = 'cells' | 'look' | 'settings'

const sideTab = useRememberedTab<SideTab>('tableEditor', ['cells', 'look', 'settings'])

const sideTabs = computed<InspectorTab<SideTab>[]>(() => [
  { id: 'cells', label: t('inspector.table.tabs.cells'), icon: TAB_ICONS.cells },
  { id: 'look', label: t('inspector.table.tabs.look'), icon: TAB_ICONS.look },
  { id: 'settings', label: t('inspector.table.tabs.settings'), icon: TAB_ICONS.settings },
])

// A cell picked, in the grid or the preview, is something to work on: its tools come forward.
watch(
  () => (target.value ? JSON.stringify(target.value) : null),
  (now, before) => {
    if (now !== null && now !== before) sideTab.value = 'cells'
  },
)

/** The buttons above the grid append, which needs no selection. */
function appendRow(): void {
  if (locked.value) return
  replace(insertRow(props.section, props.section.rows.length))
}

function appendColumn(): void {
  if (locked.value) return
  replace(insertColumn(props.section, columns.value))
}
</script>

<template>
  <div class="cells" :class="`cells--${layout}`">
    <div class="cells__side">
      <InspectorTabs
        v-if="layout === 'wide'"
        v-model:open="sideTab"
        :tabs="sideTabs"
        :label="t('inspector.table.tabs.label')"
      >
        <template #cells>
          <div class="cells__tools">
            <div class="cells__head">
              <span class="b-label">{{ t('inspector.table.cells') }}</span>
              <div class="cells__ops">
                <button
                  type="button"
                  class="b-btn b-btn--outline"
                  :disabled="locked"
                  @click="appendRow"
                >
                  + {{ t('inspector.table.row') }}
                </button>
                <button
                  type="button"
                  class="b-btn b-btn--outline"
                  :disabled="locked"
                  @click="appendColumn"
                >
                  + {{ t('inspector.table.column') }}
                </button>
              </div>
            </div>

            <TableCellToolbar
              v-if="toolbar.target"
              v-bind="toolbar"
              :target="toolbar.target"
              v-on="asks"
            />
            <div v-else class="cells__nothing">
              <svg width="22" height="22" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  v-for="(d, at) in TAB_ICONS.cells"
                  :key="at"
                  :d="d"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p class="cells__hint">{{ t('inspector.table.selectHint') }}</p>
            </div>
          </div>
        </template>
        <template #look><slot name="look" /></template>
        <template #settings><slot name="settings" /></template>
      </InspectorTabs>

      <template v-else>
        <div class="cells__head">
          <span class="b-label">{{ t('inspector.table.cells') }}</span>
          <div class="cells__ops">
            <button
              type="button"
              class="b-btn b-btn--outline"
              :disabled="locked"
              @click="appendRow"
            >
              + {{ t('inspector.table.row') }}
            </button>
            <button
              type="button"
              class="b-btn b-btn--outline"
              :disabled="locked"
              @click="appendColumn"
            >
              + {{ t('inspector.table.column') }}
            </button>
          </div>
        </div>

        <TableCellToolbar
          v-if="toolbar.target"
          v-bind="toolbar"
          :target="toolbar.target"
          v-on="asks"
        />
        <p v-else class="cells__hint">{{ t('inspector.table.selectHint') }}</p>
      </template>
    </div>

    <div class="cells__main">
      <TableCellGrid
        class="cells__grid"
        :section="section"
        :target="target"
        @select="(chosen) => (selected = chosen)"
        @edit="editCell"
      />
      <slot name="below" :target="target" :pick="pick" />
    </div>
  </div>
</template>

<style scoped>
.cells,
.cells__side,
.cells__main {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.cells__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cells__ops {
  display: flex;
  gap: var(--sp-3);
}

.cells__ops .b-btn {
  height: 24px;
  padding: 0 var(--sp-4);
  font-size: var(--text-sm);
}

.cells__hint {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.45;
}

.cells__tools {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

/* Nothing picked: where to start, in the place the tools will stand. */
.cells__nothing {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-7) var(--sp-5);
  border: 1px dashed var(--hairline-strong);
  border-radius: var(--radius);
  color: var(--ink-faint);
  text-align: center;
}

/* ---------- Wide: tools in a column, the grid and what is under it beside ---------- */

.cells--wide {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 0;
  height: 100%;
  min-height: 0;
}

.cells--wide .cells__side {
  --tabs-inset-x: var(--sp-6);
  --tabs-inset-top: var(--sp-6);

  gap: var(--sp-5);
  min-height: 0;
  padding: var(--sp-6);
  overflow-y: auto;
  border-right: 1px solid var(--hairline);
  background: var(--panel);
}

.cells--wide .cells__main {
  gap: 0;
  min-height: 0;
}

/* The grid takes what it needs up to a little over half the height, and
   scrolls past that, so the preview under it is never pushed out of sight by
   a long table. */
.cells--wide .cells__grid {
  flex: 0 1 auto;
  max-height: 56%;
  margin: var(--sp-6) var(--sp-6) var(--sp-5);
}
</style>
